import { useState, type MouseEvent, type ReactNode } from 'react';
import { LayersRowMenu } from '@/common/presentation/components/layers';
import { useCanvasStructure, type CanvasSlot } from './CanvasStructureContext';

export interface CanvasAddElementMenuProps {
  slot: CanvasSlot;
  testId: string;
  /** The trigger; receives the click handler and the open state for `aria-expanded`. */
  renderTrigger: (props: { onClick: (event: MouseEvent<HTMLElement>) => void; expanded: boolean }) => ReactNode;
}

/**
 * The canvas "add an element here" menu (Heading, Subheading, Label + value, Variable, Text, Image,
 * Logo) - shared by an empty column's "+ Add element" and the insertion line between elements.
 * Picking an option inserts that element at `slot`. Renders nothing outside the editing canvas.
 */
export function CanvasAddElementMenu({ slot, testId, renderTrigger }: CanvasAddElementMenuProps) {
  const api = useCanvasStructure();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!api) return null;

  const items = api.addOptions.map((option) => ({
    label: option.label,
    icon: option.icon,
    testId: `${testId}:option-${option.style}`,
    onClick: () => {
      setAnchor(null);
      api.insertElement(option.style, slot);
    }
  }));

  return (
    <>
      {renderTrigger({
        expanded: Boolean(anchor),
        onClick: (event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }
      })}
      <LayersRowMenu anchorEl={anchor} onClose={() => setAnchor(null)} items={items} testId={`${testId}:menu`} />
    </>
  );
}
